import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, Stethoscope } from 'lucide-react';
import { forgotPassword } from '../../api/auth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { toast } from 'react-toastify';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const res = await forgotPassword({ email: data.email });
      setEmail(data.email);
      setIsSuccess(true);
      toast.success('OTP sent successfully! Check your email.');
      
      // Navigate to OTP verification page after 2 seconds
      setTimeout(() => {
        navigate('/verify-otp', { 
          state: { email: data.email, fromForgotPassword: true }
        });
      }, 2000);
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center items-center mb-4">
              <div className="bg-green-500 rounded-full p-3">
                <Mail className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Check Your Email</h1>
            <p className="text-muted-foreground">We've sent an OTP to <strong>{email}</strong></p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="animate-pulse bg-green-100 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium">OTP sent successfully!</p>
              </div>
              <p className="text-muted-foreground text-sm">
                Please check your email for the 6-digit verification code. 
                You'll be redirected to the verification page shortly.
              </p>
            </div>

            <Button
              onClick={() => navigate('/verify-otp', { 
                state: { email, fromForgotPassword: true }
              })}
              className="w-full mb-4"
            >
              Continue to Verification
            </Button>

            <Link 
              to="/signin" 
              className="text-sm text-muted-foreground hover:text-foreground flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <div className="bg-primary rounded-full p-3">
              <Stethoscope className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">SmartCare HMS</h1>
          <p className="text-muted-foreground">Forgot your password? No worries!</p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-foreground">Forgot Password</h2>
            <p className="text-muted-foreground mt-1">Enter your email to receive an OTP</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your registered email"
                icon={Mail}
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={errors.email?.message}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sending OTP...
                </div>
              ) : (
                'Send OTP'
              )}
            </Button>
          </form>

          {/* Back to Sign In */}
          <div className="text-center mt-6">
            <Link
              to="/signin"
              className="text-primary hover:text-primary/80 font-medium text-sm flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            © 2024 SmartCare HMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
